/**
 * Minimal Shadcn-tarzı UI primitives — Button, Input, Label, Card, FormError
 *
 * Daha sonra packages/ui'ya taşınacak.
 */
import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type LabelHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@swskills/ui';

// ─── Button ─────────────────────────────────────────────────────────
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const buttonVariants: Record<ButtonVariant, string> = {
  primary:
    'bg-blue-900 hover:bg-blue-800 text-white shadow-sm disabled:bg-slate-300 disabled:cursor-not-allowed',
  secondary:
    'bg-white hover:bg-blue-50 text-blue-900 border-2 border-blue-900 disabled:border-slate-300 disabled:text-slate-400',
  ghost: 'text-slate-700 hover:bg-slate-100',
  danger: 'bg-red-600 hover:bg-red-700 text-white',
};
const buttonSizes: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-5 text-base',
  lg: 'h-12 px-6 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading, className, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
        buttonVariants[variant],
        buttonSizes[size],
        className,
      )}
      {...props}
    >
      {loading && (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
          <path d="M12 2 a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      )}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

// ─── Input ──────────────────────────────────────────────────────────
interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, error, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        'h-11 w-full rounded-lg border bg-white px-3 text-base text-slate-900 placeholder-slate-400 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1',
        error
          ? 'border-red-500 focus:ring-red-500'
          : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

// ─── Label ──────────────────────────────────────────────────────────
export const Label = forwardRef<HTMLLabelElement, LabelHTMLAttributes<HTMLLabelElement>>(
  ({ className, children, ...props }, ref) => (
    <label
      ref={ref}
      className={cn('block text-sm font-medium text-slate-700 mb-1.5', className)}
      {...props}
    >
      {children}
    </label>
  ),
);
Label.displayName = 'Label';

// ─── Field (Label + Input + Error wrapper) ──────────────────────────
interface FieldProps {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}

export function Field({ label, htmlFor, error, hint, required, children }: FieldProps): JSX.Element {
  return (
    <div className="mb-4">
      <Label htmlFor={htmlFor}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      {children}
      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      {hint && !error && <p className="mt-1 text-sm text-slate-500">{hint}</p>}
    </div>
  );
}

// ─── Card ───────────────────────────────────────────────────────────
interface CardProps {
  className?: string;
  children: ReactNode;
}

export function Card({ className, children }: CardProps): JSX.Element {
  return (
    <div className={cn('rounded-xl border border-slate-200 bg-white p-6 shadow-sm', className)}>
      {children}
    </div>
  );
}

// ─── Checkbox ───────────────────────────────────────────────────────
interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode;
  error?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, id, className, ...props }, ref) => (
    <div className="mb-3">
      <label
        htmlFor={id}
        className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer"
      >
        <input
          ref={ref}
          type="checkbox"
          id={id}
          className={cn(
            'mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-900 focus:ring-blue-500',
            className,
          )}
          {...props}
        />
        <span className="leading-snug">{label}</span>
      </label>
      {error && <p className="mt-1 ml-6 text-sm text-red-600">{error}</p>}
    </div>
  ),
);
Checkbox.displayName = 'Checkbox';

// ─── Alert ──────────────────────────────────────────────────────────
interface AlertProps {
  variant?: 'info' | 'success' | 'warning' | 'danger';
  children: ReactNode;
}

const alertVariants = {
  info: 'bg-blue-50 border-blue-200 text-blue-900',
  success: 'bg-green-50 border-green-200 text-green-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  danger: 'bg-red-50 border-red-200 text-red-900',
};

export function Alert({ variant = 'info', children }: AlertProps): JSX.Element {
  return (
    <div
      className={cn(
        'rounded-lg border px-4 py-3 text-sm',
        alertVariants[variant],
      )}
      role="alert"
    >
      {children}
    </div>
  );
}
